import type { ByoncPolicy, CodeWorkspace, GitProvider, WsSource } from "@/types";

/**
 * Mock cloud dev-workspaces. Shaped exactly like the API payload we expect to
 * fetch later — every field here maps to a real concept (repo, branch, env,
 * linked product, BYONC routing, Garlic specialization, linked integrations).
 */
export const CODE_WORKSPACES: CodeWorkspace[] = [
  {
    id: "ws-checkout",
    name: "Agent-Ready Checkout",
    repo: "payments-api",
    provider: "github",
    branch: "feature/agent-ready-checkout",
    baseBranch: "main",
    product: "ares",
    env: "cloud",
    status: "active",
    lastOpened: "now",
    lastVerified: "8 min ago",
    openTasks: 4,
    activeAgents: 2,
    lang: "TypeScript",
    accent: "#3b82f6",
    commitsBehind: 3,
    garlic: "active",
    byonc: "cost-optimized",
    integrations: ["github", "jira", "slack", "datadog", "aws"],
    usage: { disk: "4.2 GB", runtime: "2h 14m", cpu: 0.38, mem: 0.52 },
    secretsProfile: "payments-prod-readonly",
    compute: "4 vCPU · 16 GB",
    persistent: true,
    autoVerify: true,
    commits: [
      { hash: "a91f2c4", msg: "Add idempotency keys to checkout intent", author: "Priya N.", time: "12m" },
      { hash: "77be019", msg: "Wire retry policy into payment gateway", author: "ARES", time: "44m" },
      { hash: "c30a8d1", msg: "Extract provider adapter interface", author: "Marco S.", time: "2h" },
      { hash: "f1029bb", msg: "Snapshot: pre-refactor baseline", author: "Priya N.", time: "5h" },
    ],
    agents: [
      { name: "Verifier-7", task: "Proving checkout idempotency", status: "running" },
      { name: "Patch-Bot", task: "Fixing flaky retry test", status: "running" },
    ],
    verifications: [
      { label: "Idempotency proof", result: "pass", time: "8m" },
      { label: "Contract: PaymentIntent", result: "pass", time: "26m" },
      { label: "Retry storm regression", result: "fail", time: "1h" },
      { label: "Coverage gate · 86%", result: "pass", time: "1h" },
    ],
    timeline: [
      { label: "Agent spawned", detail: "Verifier-7 picked up PAY-431", time: "8m" },
      { label: "Branch synced", detail: "3 commits ahead of main", time: "44m" },
      { label: "Snapshot saved", detail: "Before ARES refactor", time: "5h" },
      { label: "Workspace resumed", detail: "from sleeping state", time: "6h" },
    ],
    snapshots: [
      { id: "snap-1", name: "Before ARES refactor", createdAt: "5h ago", size: "3.8 GB", verified: true, branch: "feature/agent-ready-checkout" },
      { id: "snap-2", name: "Post verification pass", createdAt: "1d ago", size: "3.9 GB", verified: true, branch: "feature/agent-ready-checkout" },
      { id: "snap-3", name: "Garlic specialization baseline", createdAt: "3d ago", size: "3.6 GB", verified: false, branch: "main" },
    ],
    suggestions: [
      { id: "sg-1", tone: "warning", text: "ARES verification has failed twice on this branch.", action: "Review failures" },
      { id: "sg-2", tone: "info", text: "Branch is 3 commits behind main.", action: "Sync branch" },
      { id: "sg-3", tone: "success", text: "Garlic has enough repo activity to refresh specialization.", action: "Refresh Garlic" },
    ],
    health: { build: "passing", tests: "1 flaky", deps: "up to date" },
  },
  {
    id: "ws-mobile",
    name: "Mobile Checkout Revamp",
    repo: "mobile-checkout",
    provider: "github",
    branch: "feat/native-pay-sheet",
    baseBranch: "develop",
    product: "athena",
    env: "ephemeral",
    status: "needs-sync",
    lastOpened: "2h ago",
    lastVerified: "1d ago",
    openTasks: 6,
    activeAgents: 0,
    lang: "Swift",
    accent: "#f97316",
    commitsBehind: 19,
    garlic: "available",
    byonc: "local-preferred",
    integrations: ["github", "jira", "slack", "vercel"],
    usage: { disk: "2.1 GB", runtime: "0m", cpu: 0, mem: 0 },
    secretsProfile: "mobile-staging",
    compute: "2 vCPU · 8 GB",
    persistent: false,
    autoVerify: false,
    commits: [
      { hash: "b22f0aa", msg: "Native Apple Pay sheet scaffolding", author: "Dana K.", time: "1d" },
      { hash: "9c01d77", msg: "Refactor cart reducer", author: "Dana K.", time: "2d" },
      { hash: "ee71b30", msg: "Bump checkout SDK to 4.1", author: "Lee W.", time: "3d" },
    ],
    agents: [],
    verifications: [
      { label: "UI snapshot suite", result: "pass", time: "1d" },
      { label: "Payment sheet a11y", result: "fail", time: "1d" },
    ],
    timeline: [
      { label: "Workspace slept", detail: "idle for 2h", time: "2h" },
      { label: "Drift detected", detail: "19 commits behind develop", time: "5h" },
      { label: "Snapshot saved", detail: "SDK upgrade checkpoint", time: "3d" },
    ],
    snapshots: [
      { id: "snap-4", name: "SDK 4.1 upgrade", createdAt: "3d ago", size: "2.0 GB", verified: true, branch: "feat/native-pay-sheet" },
    ],
    suggestions: [
      { id: "sg-4", tone: "warning", text: "Branch is 19 commits behind develop.", action: "Sync branch" },
      { id: "sg-5", tone: "info", text: "This workspace has 3 stale tasks. Consider archiving.", action: "Review tasks" },
    ],
    health: { build: "passing", tests: "2 failing", deps: "1 outdated" },
  },
  {
    id: "ws-readiness",
    name: "API Readiness Service",
    repo: "ai-readiness-service",
    provider: "gitlab",
    branch: "main",
    baseBranch: "main",
    product: "athena",
    env: "cloud",
    status: "verifying",
    lastOpened: "12m ago",
    lastVerified: "now",
    openTasks: 2,
    activeAgents: 1,
    lang: "Python",
    accent: "#22c55e",
    commitsBehind: 0,
    garlic: "active",
    byonc: "cloud-fallback",
    integrations: ["gitlab", "jira", "slack", "datadog", "gcp"],
    usage: { disk: "5.6 GB", runtime: "4h 02m", cpu: 0.71, mem: 0.64 },
    secretsProfile: "readiness-svc",
    compute: "8 vCPU · 32 GB",
    persistent: true,
    autoVerify: true,
    commits: [
      { hash: "1190fac", msg: "Generate llms.txt for 38 endpoints", author: "ATHENA", time: "4m" },
      { hash: "33ce201", msg: "Add MCP manifest exporter", author: "Sven O.", time: "1h" },
      { hash: "aa0b9e2", msg: "Schema drift detector v2", author: "Sven O.", time: "3h" },
    ],
    agents: [{ name: "Spec-Synth", task: "Generating OpenAPI for billing-svc", status: "running" }],
    verifications: [
      { label: "OpenAPI ↔ impl parity", result: "pass", time: "now" },
      { label: "llms.txt freshness", result: "pass", time: "4m" },
      { label: "MCP manifest valid", result: "pass", time: "1h" },
    ],
    timeline: [
      { label: "Verification started", detail: "full readiness sweep", time: "now" },
      { label: "Specialization refreshed", detail: "Garlic · +1.8% accuracy", time: "2h" },
      { label: "Workspace opened", detail: "from snapshot", time: "12m" },
    ],
    snapshots: [
      { id: "snap-5", name: "Readiness 100% baseline", createdAt: "2d ago", size: "5.4 GB", verified: true, branch: "main" },
    ],
    suggestions: [
      { id: "sg-6", tone: "success", text: "Specialization profile is active and current.", action: "View Garlic" },
      { id: "sg-7", tone: "info", text: "All linked integrations are healthy.", action: "View integrations" },
    ],
    health: { build: "passing", tests: "all green", deps: "up to date" },
  },
  {
    id: "ws-portal",
    name: "Customer Portal Hardening",
    repo: "customer-portal",
    provider: "github",
    branch: "fix/session-revocation",
    baseBranch: "main",
    product: "ares",
    env: "vpc",
    status: "conflict",
    lastOpened: "35m ago",
    lastVerified: "3h ago",
    openTasks: 5,
    activeAgents: 0,
    lang: "TypeScript",
    accent: "#a855f7",
    commitsBehind: 7,
    garlic: "available",
    byonc: "private-only",
    integrations: ["github", "jira", "slack", "sentry", "aws"],
    usage: { disk: "3.4 GB", runtime: "1h 20m", cpu: 0.12, mem: 0.31 },
    secretsProfile: "portal-vpc",
    compute: "4 vCPU · 16 GB",
    persistent: true,
    autoVerify: true,
    commits: [
      { hash: "5f8c1aa", msg: "Revoke sessions on password reset", author: "Aria L.", time: "35m" },
      { hash: "0db44e1", msg: "Merge main (conflicts in auth/)", author: "Aria L.", time: "1h" },
    ],
    agents: [],
    verifications: [
      { label: "Session revocation proof", result: "pass", time: "3h" },
      { label: "CSRF regression", result: "pass", time: "3h" },
    ],
    timeline: [
      { label: "Merge conflict", detail: "auth/session.ts ↔ main", time: "1h" },
      { label: "Snapshot saved", detail: "Pre-merge checkpoint", time: "1h" },
    ],
    snapshots: [
      { id: "snap-6", name: "Pre-merge checkpoint", createdAt: "1h ago", size: "3.3 GB", verified: true, branch: "fix/session-revocation" },
    ],
    suggestions: [
      { id: "sg-8", tone: "warning", text: "Merge conflict in auth/session.ts blocks verification.", action: "Resolve conflict" },
      { id: "sg-9", tone: "info", text: "Private-only routing is active for this VPC workspace.", action: "View BYONC" },
    ],
    health: { build: "blocked", tests: "—", deps: "up to date" },
  },
  {
    id: "ws-infra",
    name: "Infra Orchestrator",
    repo: "infra-orchestrator",
    provider: "gitlab",
    branch: "main",
    baseBranch: "main",
    product: "byonc",
    env: "cloud",
    status: "sleeping",
    lastOpened: "1d ago",
    lastVerified: "1d ago",
    openTasks: 1,
    activeAgents: 0,
    lang: "Go",
    accent: "#10b981",
    commitsBehind: 2,
    garlic: "none",
    byonc: "local-preferred",
    integrations: ["gitlab", "kubernetes", "aws", "datadog", "pagerduty"],
    usage: { disk: "1.2 GB", runtime: "0m", cpu: 0, mem: 0 },
    secretsProfile: "infra-ops",
    compute: "2 vCPU · 8 GB",
    persistent: true,
    autoVerify: false,
    commits: [
      { hash: "c771a02", msg: "Rebalance node pools across regions", author: "Router-Prime", time: "1d" },
      { hash: "21de9f0", msg: "Add cost-policy guardrails", author: "Tom B.", time: "2d" },
    ],
    agents: [],
    verifications: [{ label: "Terraform plan parity", result: "pass", time: "1d" }],
    timeline: [
      { label: "Workspace slept", detail: "idle for 1d", time: "1d" },
      { label: "Routing rebalanced", detail: "BYONC saved $4.20/h", time: "1d" },
    ],
    snapshots: [],
    suggestions: [
      { id: "sg-10", tone: "info", text: "Workspace has been sleeping for 1 day.", action: "Resume" },
    ],
    health: { build: "passing", tests: "all green", deps: "2 outdated" },
  },
  {
    id: "ws-routing",
    name: "Model Routing Gateway",
    repo: "model-routing-gateway",
    provider: "github",
    branch: "feat/hybrid-router-v3",
    baseBranch: "main",
    product: "byonc",
    env: "local",
    status: "error",
    lastOpened: "5m ago",
    lastVerified: "2h ago",
    openTasks: 3,
    activeAgents: 1,
    lang: "Rust",
    accent: "#f43f5e",
    commitsBehind: 1,
    garlic: "active",
    byonc: "private-only",
    integrations: ["github", "slack", "docker", "kubernetes"],
    usage: { disk: "6.8 GB", runtime: "3h 41m", cpu: 0.88, mem: 0.79 },
    secretsProfile: "routing-local",
    compute: "8 vCPU · 32 GB",
    persistent: true,
    autoVerify: true,
    commits: [
      { hash: "98a1c30", msg: "Hybrid router scoring function v3", author: "Router-Prime", time: "5m" },
      { hash: "44b0f12", msg: "Fix panic on empty cost policy", author: "Nora P.", time: "1h" },
    ],
    agents: [{ name: "Router-Prime", task: "Rebalancing inference across 3 regions", status: "running" }],
    verifications: [
      { label: "Routing latency budget", result: "fail", time: "2h" },
      { label: "Cost policy fuzz", result: "pass", time: "2h" },
    ],
    timeline: [
      { label: "Build error", detail: "linker failure on llvm-18", time: "5m" },
      { label: "Agent spawned", detail: "Router-Prime rebalancing", time: "1h" },
    ],
    snapshots: [
      { id: "snap-7", name: "Router v2 stable", createdAt: "4d ago", size: "6.5 GB", verified: true, branch: "main" },
    ],
    suggestions: [
      { id: "sg-11", tone: "warning", text: "Build is failing — linker error on llvm-18.", action: "View logs" },
      { id: "sg-12", tone: "info", text: "Fork from 'Router v2 stable' snapshot to bisect.", action: "Fork snapshot" },
    ],
    health: { build: "failing", tests: "1 failing", deps: "up to date" },
  },
];

export const WS_ENV_LABEL: Record<CodeWorkspace["env"], string> = {
  local: "Local",
  cloud: "Cloud",
  vpc: "VPC",
  ephemeral: "Ephemeral",
};

export const WS_STATUS_LABEL: Record<CodeWorkspace["status"], string> = {
  active: "Active",
  sleeping: "Sleeping",
  "needs-sync": "Needs Sync",
  conflict: "Conflict",
  verifying: "Verifying",
  error: "Error",
};

export const WS_STATUS_TONE: Record<CodeWorkspace["status"], string> = {
  active: "connected",
  sleeping: "paused",
  "needs-sync": "syncing",
  conflict: "degraded",
  verifying: "running",
  error: "error",
};

export const WS_PROVIDER_LABEL: Record<WsSource, string> = {
  github: "GitHub",
  gitlab: "GitLab",
  bitbucket: "Bitbucket",
  local: "Local folder",
  s3: "S3 bucket",
};

/** Sources that clone a git repository (repo + branch steps apply). */
export function isGitSource(provider: WsSource): provider is GitProvider {
  return provider === "github" || provider === "gitlab" || provider === "bitbucket";
}

/** Client-side mirror of the server's built-in routing modes — the fallback
 *  when `/api/v1/byonc/policies` is unreachable (e.g. an older API build). */
export const BUILTIN_BYONC_POLICIES: ByoncPolicy[] = [
  { id: "local-preferred", name: "Local preferred", description: "Prefer on-prem capacity; burst to cloud when saturated", builtin: true },
  { id: "cloud-fallback", name: "Cloud fallback", description: "Local first, automatic cloud fallback on errors or queue depth", builtin: true },
  { id: "cost-optimized", name: "Cost optimized", description: "Cheapest capacity that meets the latency budget", builtin: true },
  { id: "private-only", name: "Private only", description: "Never leaves the VPC — private endpoints only", builtin: true },
];

const BUILTIN_BYONC_NAMES = new Map(BUILTIN_BYONC_POLICIES.map((p) => [p.id, p.name]));

/** Display name for a workspace's routing policy id, whatever its origin. */
export function byoncPolicyLabel(id: string, policies?: ByoncPolicy[]): string {
  return policies?.find((p) => p.id === id)?.name ?? BUILTIN_BYONC_NAMES.get(id) ?? id;
}
